import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { BUSINESS } from "../../lib/constants";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-login-page">
      <Helmet><title>Admin Login | Orion Services LLC</title></Helmet>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm border border-amber-500/15 rounded-md p-8 bg-carbon">
        <div className="flex items-center gap-3 mb-8">
          <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-10 w-10 rounded-full object-cover" />
          <div>
            <p className="font-display text-lg text-white leading-none">ORION ADMIN</p>
            <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><Lock className="h-3 w-3" /> Restricted Access</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Email</Label>
            <Input required type="email" data-testid="admin-login-username" value={email} onChange={(e) => setEmail(e.target.value)} className="!bg-obsidian border-amber-500/15" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Password</Label>
            <Input required type="password" data-testid="admin-login-password" value={password} onChange={(e) => setPassword(e.target.value)} className="!bg-obsidian border-amber-500/15" />
          </div>
          {error && <p className="text-rose-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="w-full bg-amber-light text-obsidian hover:bg-white font-semibold uppercase tracking-wide">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
          <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link" className="block text-center text-xs text-zinc-500 hover:text-amber-light font-mono uppercase tracking-wide">
            Forgot Password?
          </Link>
        </form>
      </motion.div>
    </div>
  );
}
